import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildChart, chartSize, crownPath, surfaceRects, surfacesForTooth,
  TOOTH_W, TOOTH_H,
} from './geometry/toothGeometry';
import styles from './demos.module.css';

const TEETH = buildChart('PERMANENT');
const SIZE = chartSize('PERMANENT');

/**
 * The dental chart, live on the marketing page.
 *
 * Geometry is the product's own (see ./geometry), so what a visitor clicks here
 * is the chart a clinician uses. `dir="ltr"` is pinned on the <svg> because the
 * page flips under Hebrew and Arabic and anatomy must not: the viewer's left is
 * the patient's right, and mirroring the chart mislabels all 32 teeth.
 *
 * The initial* props exist so a test can start from a known state. Visitors get
 * the defaults, which show a chart that already has findings on it — an empty
 * chart reads as a broken widget rather than as a tool.
 */
const ToothChartDemo = ({
  color = '#0ea5e9',
  initialSelected = '16',
  initialMarks = { 16: ['O'], 26: ['M', 'O'], 36: ['B'] },
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(initialSelected);
  const [marks, setMarks] = useState(initialMarks);

  const toggleSurface = (fdi, surface) => {
    setMarks((prev) => {
      const on = prev[fdi] || [];
      const next = on.includes(surface)
        ? on.filter((s) => s !== surface)
        : [...on, surface];
      return { ...prev, [fdi]: next };
    });
  };

  const markedCount = Object.values(marks).filter((s) => s && s.length).length;

  return (
    <div className={styles.demoFrame} data-demo="dental-charting">
      <svg
        dir="ltr"
        className={styles.toothSvg}
        viewBox={`-4 -4 ${SIZE.width + 8} ${SIZE.height + 8}`}
        role="group"
        aria-label={t('demos.tooth.chartAria')}
      >
        {TEETH.map((tooth) => {
          const isSelected = selected === tooth.fdi;
          const marked = marks[tooth.fdi] || [];
          const rects = surfaceRects(tooth);
          return (
            <g key={tooth.fdi} transform={`translate(${tooth.x},${tooth.y})`}>
              <path
                className={styles.crown}
                d={crownPath(tooth)}
                fill={marked.length ? `${color}22` : '#fff'}
                stroke={isSelected ? color : '#cbd5e1'}
                strokeWidth={isSelected ? 1.8 : 1}
                role="button"
                tabIndex={0}
                aria-label={t('demos.tooth.aria', { fdi: tooth.fdi })}
                aria-pressed={isSelected}
                onClick={() => setSelected(isSelected ? null : tooth.fdi)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(isSelected ? null : tooth.fdi);
                  }
                }}
              />
              {isSelected && surfacesForTooth(tooth).map((s) => {
                const r = rects[s];
                const on = marked.includes(s);
                return (
                  <rect
                    key={s}
                    className={styles.surface}
                    x={r.x * TOOTH_W}
                    y={r.y * TOOTH_H}
                    width={r.w * TOOTH_W}
                    height={r.h * TOOTH_H}
                    rx={1.5}
                    fill={on ? color : 'transparent'}
                    stroke={color}
                    strokeWidth={0.6}
                    role="button"
                    tabIndex={0}
                    aria-label={t('demos.tooth.surfaceAria', { surface: t(`demos.tooth.${s}`), fdi: tooth.fdi })}
                    aria-pressed={on}
                    onClick={() => toggleSurface(tooth.fdi, s)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSurface(tooth.fdi, s);
                      }
                    }}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className={styles.demoFoot}>
        <span className={styles.demoPill} style={{ background: `${color}18`, color }}>
          {t('demos.tooth.fdi')}
        </span>
        <span className={styles.demoPill} style={{ background: `${color}18`, color }}>
          {t('demos.tooth.charted', { count: markedCount })}
        </span>
      </div>
    </div>
  );
};

export default ToothChartDemo;
